<template>
  <div class="AddressProposals">
    <i
      class="icon-localizacion"
      id="location"
    />

    <h3 class="address-question-title gl_form-title small">
      {{ addressQuestionTitle }}
    </h3>
    <RadioButtonGroup
      v-if="possibleAddresses.length === 2"
      id="addresses"
      :key="key"
      :value="addressSelected"
      :options="possibleAddresses"
      :option-labels="[
        t('copy_address-form_address-generated'),
        t('copy_address-form_address-normalized')
      ]"
      v-model="addressSelected"
      :rules="[validationRules.radioRequired]"
    />

    <div
      v-else
      class="unique-address"
    >
      <div v-html="possibleAddresses[0].label" />

      <div class="line" />
    </div>

    <p class="light-label">
      {{ addressRevertText }}
    </p>

    <div class="action">
      <BaseButton
        @click="emit('clear')"
        type="transparent"
      >
        {{ t('copy_address-form_button_return-and-edit') }}
      </BaseButton>
    </div>

    <div class="line" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type PropType, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRules } from '@/modules/core/composables/useRulesComposable'
import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import { AddressTypeEnum } from '@/modules/core/models/enums'
import type { OptionsValuesInterface } from '@/modules/core/models/types'

const { t } = useI18n()
const { required } = useRules()

interface PropsInterface {
  generatedAddress: AddressDetails | null
  normalizedAddress: AddressDetails | null
}
const props = withDefaults(defineProps<PropsInterface>(), {})

const validationRules = {
  radioRequired: (value: string) => required(value, 'common.validations.option')
}

const emit = defineEmits(['clear'])

const addressSelected = defineModel({ type: String as PropType<AddressTypeEnum | null> })
const key = ref(Date.now())

const addressQuestionTitle = computed(() => {
  if (props.normalizedAddress) {
    return t('copy_address-form_confirm-your-address')
  }

  return t('copy_address-form_confirm-your-address-generated')
})

const possibleAddresses: Ref<OptionsValuesInterface[]> = computed(() => {
  const generatedAddressLabel: string = getAddressLabel(props.generatedAddress)
  const normalizedAddressLabel: string = getAddressLabel(props.normalizedAddress)
  const areEquals = compareAddresses(props.generatedAddress, props.normalizedAddress)

  const generatedOption = { label: generatedAddressLabel, value: AddressTypeEnum.GENERATED }
  const normalizedOption = { label: normalizedAddressLabel, value: AddressTypeEnum.NORMALIZED }

  if (!normalizedAddressLabel) {
    return [generatedOption]
  }

  if (areEquals) {
    // Puede que la generada tenga el tipo de calle metido en el nombre de la direccion con el atributo a null
    return [normalizedOption]
  }

  return [generatedOption, normalizedOption]
})

const addressRevertText = computed(() => {
  const generatedAddressLabel = getAddressLabel(props.generatedAddress)
  const normalizedAddressLabel = getAddressLabel(props.normalizedAddress)
  if (props.normalizedAddress && generatedAddressLabel !== normalizedAddressLabel) {
    return t('copy_address-form_none-addresses')
  }

  return t('copy_address-form_no-your-address')
})

function compareAddresses(address1: AddressDetails | null, address2: AddressDetails | null) {
  if (!address1 || !address2) return false

  const street1 = `${address1.streetType ?? ''} ${address1.address}`.normalize("NFC").trim()
  const street2 = `${address2.streetType ?? ''} ${address2.address}`.trim()

  return (
    street1.toUpperCase() === street2.toUpperCase() &&
    address1.postalCode === address2.postalCode &&
    address1.city === address2.city &&
    address1.province === address2.province
  )
}

function getAddressLabel(address: AddressDetails | any) {
  if (!address) return ''

  let firstLine = `${address.streetType ?? ''} ${address.address ?? ''}`.trim()
  firstLine += address.roadNumber ? `, ${address.roadNumber}` : ''
  firstLine += address.gateway ? `, Portal ${address.gateway}` : ''
  firstLine += address.stair ? `, Esc. ${address.stair}` : ''
  firstLine += address.floor ? `, Piso ${address.floor}` : ''
  firstLine += address.doorway ? `, Pta ${address.doorway}` : ''
  firstLine += address.addressExtra ? `, ${address.addressExtra}` : ''

  const secondLine = `${address.postalCode}, ${address.city}`
  const thirdLine = `${address.province}`

  return `${firstLine} <br/> ${secondLine} <br/> ${thirdLine}`
}

function clearValue() {
  addressSelected.value = null
  key.value = Date.now()
}

defineExpose({
  clearValue
})
</script>

<style lang="scss" scoped>
.AddressProposals {
  .line {
    border: 1px $color-neutral-light solid;
    margin: $spacing-xl 0;
  }

  i {
    font-size: 4rem;
    display: block;
    text-align: center;
    margin-top: $spacing-xl;
    margin-bottom: $spacing-l;
  }

  .address-question-title {
    text-align: center;
    text-wrap: balance;
    margin-bottom: $spacing-xl;
  }

  .unique-address {
    text-align: center;
    color: $color-neutral-dark;
  }

  .light-label {
    color: $color-neutral;
    text-align: center;
  }
}
</style>
